/**
 * R5 — RP67 → RP68 : avenants dans l'onglet Marchés (architecte, collaborateurs, AMO).
 *
 * Statuts : Proposé → Accepté par le MOA · ou Refusé (09-VOCABULAIRE). L'architecte
 * prépare l'avenant dans la DPGF (Conception › DPGF › Avenants) et le soumet au client ;
 * le client l'accepte ou le refuse avec un code de vérification. L'AMO peut donner un
 * avis (facultatif) tant que l'avenant attend l'accord du client.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePlus } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import AvenantFiche from './AvenantFiche';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVIS = [
  { cle: 'favorable', libelle: 'Favorable' },
  { cle: 'reserve', libelle: 'Réservé' },
  { cle: 'defavorable', libelle: 'Défavorable' },
];

function AvisAmoForm({ chantierId, avenant, onDone }) {
  const [avis, setAvis] = useState(avenant.avis_amo?.avis || '');
  const [commentaire, setCommentaire] = useState(avenant.avis_amo?.commentaire || '');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    if (!avis) { toast.error('Choisissez un avis'); return; }
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/avenants/${avenant.id}/avis-amo`,
        { avis, commentaire }, { withCredentials: true });
      toast.success('Avis enregistré');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement de l'avis");
    } finally { setEnvoi(false); }
  };
  return (
    <div className="border-t border-[#E0E0DB] px-4 py-3 bg-[#F5F0FB]" data-testid={`avenant-avis-form-${avenant.id}`}>
      <p className="text-xs font-bold text-[#4B3A6B] mb-2">Mon avis sur cet avenant (facultatif)</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {AVIS.map((a) => (
          <button key={a.cle} type="button" onClick={() => setAvis(a.cle)}
            className={`text-xs px-3 py-1 border ${avis === a.cle ? 'bg-[#7B5EA7] text-white border-[#7B5EA7]' : 'bg-white border-[#D6C8E8] text-[#4B3A6B]'}`}
            data-testid={`avenant-avis-${a.cle}-${avenant.id}`}>
            {a.libelle}
          </button>
        ))}
      </div>
      <Textarea rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
        placeholder="Commentaire (visible de l'architecte et du client)" className="rounded-none text-sm" />
      <Button size="sm" className="rounded-none mt-2 bg-[#7B5EA7] hover:bg-[#5e4585] text-white" disabled={envoi} onClick={envoyer}>
        Enregistrer mon avis
      </Button>
    </div>
  );
}

export default function AvenantsMarche({ chantierId, roleProjet, onChanged }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/avenants`, { withCredentials: true });
      setData(d);
    } catch { setData({ items: [] }); }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !(data.items || []).length) return null;
  const estAmo = roleProjet === 'amo';
  const items = estAmo ? data.items.filter((a) => a.fiche) : data.items;
  if (!items.length) return null;

  return (
    <div className="mt-6" data-testid="marches-avenants">
      <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-1 flex items-center gap-2">
        <FilePlus size={14} /> Avenants
      </h3>
      <p className="text-xs text-[#6B6B6B] mb-3">
        Un avenant engage le client : il est accepté ou refusé par le client (code de vérification).
        {!estAmo && ' Créez-le avec « Nouvel avenant » sur la carte du lot (Suivi par lot, ci-dessus), puis soumettez-le au client.'}
      </p>
      <div className="space-y-3">
        {items.map((av) => (
          <AvenantFiche key={av.id} avenant={av}>
            {estAmo && av.statut === 'PROPOSE' && (
              <AvisAmoForm chantierId={chantierId} avenant={av} onDone={() => { load(); onChanged?.(); }} />
            )}
          </AvenantFiche>
        ))}
      </div>
    </div>
  );
}
